/**
 * GIWAHS — Admin verification queue at /admin/verifications
 * ---------------------------------------------------------------------
 * Lists all in_review supplier verification requests with a side-drawer
 * that lets the admin: review uploaded documents (signed URLs, 60s),
 * approve at a chosen tier (or override), reject with a note, or revoke
 * an already-approved supplier.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Check, X, ExternalLink, FileText, Award, AlertCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { adminClient } from '../lib/adminClient';

const STATUS_TONE = {
  in_review: 'border-amber-500 bg-amber-50 text-amber-900',
  approved:  'border-emerald-700 bg-emerald-50 text-emerald-900',
  rejected:  'border-red-500 bg-red-50 text-red-900',
  withdrawn: 'border-neutral-300 bg-neutral-50 text-neutral-700',
};

const TIER_OPTIONS = [
  { key: 'verified', label: 'Verified' },
  { key: 'gold',     label: 'Premium Gold' },
  { key: 'elite',    label: 'Enterprise Elite' },
];

const AdminVerifications = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('in_review');
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [decisionTier, setDecisionTier] = useState('verified');
  const [decisionNote, setDecisionNote] = useState('');
  const [paidFlag, setPaidFlag] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await adminClient.listVerificationRequests({ status: filter || null });
      setRows(r);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDrawer = async (row) => {
    setSelected(row);
    setDecisionTier(row.requested_tier || 'verified');
    setDecisionNote('');
    setPaidFlag(row.paid || false);
    setDocs([]);
    setDocsLoading(true);
    try {
      const d = await adminClient.getSupplierDocuments(row.supplier_id);
      setDocs(d);
    } catch (e) {
      toast.error(`Could not load docs: ${e?.message || e}`);
    } finally { setDocsLoading(false); }
  };

  const closeDrawer = () => { setSelected(null); setDocs([]); };

  const decide = async (approve) => {
    if (!selected) return;
    setActing(true);
    try {
      await adminClient.decideVerification({
        request_id:    selected.id,
        approve,
        assigned_tier: approve ? decisionTier : null,
        note:          decisionNote || null,
        paid:          paidFlag,
      });
      toast.success(approve ? `Approved at ${decisionTier.toUpperCase()}` : 'Rejected.');
      closeDrawer();
      load();
    } catch (e) {
      toast.error(`Decision failed: ${e?.message || e}`);
    } finally { setActing(false); }
  };

  const revoke = async () => {
    if (!selected) return;
    if (!confirm(`Revoke verification for ${selected.supplier_name}? Tier will drop to Unverified.`)) return;
    setActing(true);
    try {
      await adminClient.revokeVerification({ supplier_id: selected.supplier_id, note: decisionNote || null });
      toast.success('Verification revoked.');
      closeDrawer();
      load();
    } catch (e) {
      toast.error(`Revoke failed: ${e?.message || e}`);
    } finally { setActing(false); }
  };

  const openSignedDoc = async (storage_path) => {
    try {
      const url = await adminClient.signedDocUrl(storage_path, 60);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(`Could not sign URL: ${e?.message || e}`);
    }
  };

  return (
    <div data-testid="admin-verifications-page" className="mx-auto max-w-7xl px-6 py-10">
      <SEO title="Verifications · GIWAHS admin" description="Verification request queue" />

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black pb-6">
        <div>
          <p className="label-overline text-neutral-500">Admin · operations</p>
          <h1 className="mt-1 font-display text-3xl tracking-tight md:text-4xl">Verification queue</h1>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} strokeWidth={1.5} className="text-neutral-500" />
          {['in_review', 'approved', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              data-testid={`filter-${s || 'all'}`}
              onClick={() => setFilter(s)}
              className={`border px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider ${filter === s ? 'border-black bg-black text-white' : 'border-neutral-300 hover:border-black'}`}
            >
              {s || 'all'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-neutral-500" /></div>
      ) : err ? (
        <div className="mt-8 border border-red-500 bg-red-50 p-4 text-red-900"><AlertCircle className="inline mr-2" size={16} />{err}</div>
      ) : rows.length === 0 ? (
        <div data-testid="empty-queue" className="mt-8 border border-dashed border-neutral-300 p-8 text-center text-neutral-500">
          No requests match this filter.
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto border border-black bg-white">
          <table className="w-full text-sm">
            <thead className="border-b-2 border-black bg-neutral-50">
              <tr className="text-left font-mono-data text-[10px] uppercase tracking-wider text-neutral-700">
                <th className="p-3">Supplier</th><th className="p-3">Requested</th><th className="p-3">Status</th>
                <th className="p-3">Docs</th><th className="p-3">Score</th><th className="p-3">Submitted</th><th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} data-testid={`queue-row-${i}`} className={`${i < rows.length - 1 ? 'border-b border-neutral-200' : ''} hover:bg-neutral-50`}>
                  <td className="p-3"><div className="font-display text-base tracking-tight">{r.supplier_name}</div><div className="font-mono-data text-[10px] text-neutral-500">{r.supplier_slug}</div></td>
                  <td className="p-3 font-mono-data text-[11px] uppercase tracking-wider">{r.requested_tier}{r.paid && <span className="ml-1 border border-amber-700 bg-amber-50 px-1 text-[9px] text-amber-900">paid</span>}</td>
                  <td className="p-3"><span className={`inline-flex border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${STATUS_TONE[r.status] || ''}`}>{r.status}</span></td>
                  <td className="p-3 font-mono-data text-[11px]">{r.doc_count}</td>
                  <td className="p-3 font-mono-data text-[11px]">{r.trust_score}/100</td>
                  <td className="p-3 font-mono-data text-[10px] text-neutral-500">{new Date(r.submitted_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`open-${i}`} onClick={() => openDrawer(r)} className="border border-black px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white">
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <div data-testid="verif-drawer" className="fixed inset-0 z-40 flex justify-end bg-black/40">
          <div className="flex h-full w-full max-w-2xl flex-col bg-white">
            <header className="flex items-start justify-between gap-3 border-b border-black p-5">
              <div>
                <p className="label-overline text-neutral-500">Review</p>
                <h2 className="mt-1 font-display text-2xl tracking-tight">{selected.supplier_name}</h2>
                <p className="mt-1 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">{selected.supplier_slug} · requested {selected.requested_tier}</p>
              </div>
              <button data-testid="drawer-close" onClick={closeDrawer} className="text-neutral-500 hover:text-black"><X /></button>
            </header>

            <div className="flex-1 overflow-y-auto p-5">
              <section>
                <p className="label-overline text-neutral-500">Attestation</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-800">{selected.attestation || '—'}</p>
              </section>

              <section className="mt-6">
                <p className="label-overline text-neutral-500">Documents ({docs.length})</p>
                {docsLoading ? (
                  <div className="mt-3 flex items-center gap-2 text-neutral-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                ) : docs.length === 0 ? (
                  <p className="mt-3 text-sm text-neutral-500">No documents uploaded yet.</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {docs.map((d) => (
                      <li key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between gap-3 border border-neutral-300 bg-neutral-50 p-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="shrink-0 text-neutral-500" />
                            <p className="truncate font-mono-data text-[12px]">{d.file_name}</p>
                          </div>
                          <p className="mt-1 ml-6 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{d.doc_type} · {new Date(d.uploaded_at).toLocaleDateString()}</p>
                        </div>
                        <button data-testid={`open-doc-${d.id}`} onClick={() => openSignedDoc(d.storage_path)} className="inline-flex items-center gap-1 border border-black px-2 py-1 font-mono-data text-[10px] uppercase tracking-wider hover:bg-black hover:text-white">
                          Open <ExternalLink size={10} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="mt-8 border-t border-neutral-200 pt-6">
                <p className="label-overline text-neutral-500">Decision</p>

                <label className="mt-3 block font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">Assign tier on approve</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TIER_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      data-testid={`decide-tier-${opt.key}`}
                      onClick={() => setDecisionTier(opt.key)}
                      className={`border px-3 py-1.5 font-mono-data text-[10px] uppercase tracking-wider ${decisionTier === opt.key ? 'border-black bg-black text-white' : 'border-neutral-300 hover:border-black'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <label className="mt-4 inline-flex items-center gap-2 font-mono-data text-[11px] uppercase tracking-wider text-neutral-700">
                  <input type="checkbox" data-testid="paid-checkbox" checked={paidFlag} onChange={(e) => setPaidFlag(e.target.checked)} />
                  Mark as paid (Premium / Enterprise)
                </label>

                <label className="mt-4 block font-mono-data text-[10px] uppercase tracking-wider text-neutral-500" htmlFor="decision-note">Reviewer note (visible to supplier)</label>
                <textarea
                  id="decision-note"
                  data-testid="decision-note"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  rows={3}
                  className="mt-2 w-full border border-black bg-white p-3 font-mono-data text-[12px]"
                  placeholder={decisionTier === 'verified' ? 'e.g. Documents check out. Welcome to GIWAHS Verified.' : 'Required reason if rejecting.'}
                />
              </section>
            </div>

            <footer className="flex flex-wrap items-center gap-2 border-t border-black bg-neutral-50 p-4">
              <button
                data-testid="approve-btn"
                onClick={() => decide(true)}
                disabled={acting}
                className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-700 px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-emerald-800 disabled:opacity-50"
              >
                {acting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} strokeWidth={2} />} Approve at {decisionTier}
              </button>
              <button
                data-testid="reject-btn"
                onClick={() => decide(false)}
                disabled={acting}
                className="inline-flex items-center gap-1.5 border border-red-700 bg-red-700 px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider text-white hover:bg-red-800 disabled:opacity-50"
              >
                <X size={12} strokeWidth={2} /> Reject
              </button>
              <button
                data-testid="revoke-btn"
                onClick={revoke}
                disabled={acting}
                className="ml-auto inline-flex items-center gap-1.5 border border-neutral-700 px-3 py-2 font-mono-data text-[11px] uppercase tracking-wider hover:bg-neutral-700 hover:text-white disabled:opacity-50"
              >
                Revoke verification
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVerifications;
